import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MdOutlineSwapVert } from 'react-icons/md';
import TableLink from './TableLink';
import { TABLE_DATA_TYPES } from '../../utils/tableDataTypes';
import { convertUrlsToLinks } from '../../utils/urlFinder';
/* eslint-disable no-nested-ternary */
// We use typeOfData in the links href to be able to navigate to the specific site/contact/route.etc when a row is clicked
// Note that type should be one of this :    'sites',  'routes', 'contacts', 'tags', 'notes', 'seasons', 'visits','contact history','site history', 'users' or 'other'
const Table = ({
  dataSource = [],
  columns = [],
  onSort = () => { },
  typeOfData = 'other',
  isHistory = false,
  isUsingLinkWrapper = true, // When true, wraps each table cell's content in a link (<a>) for navigation / right-click options. False when rows open modals instead.
  className = 'w-full',
  DetailComponent = null,
  onClick = () => { },
  columnTextColor = undefined,
  defaultSortOrder = {},
  isRowClickable = () => true,
}) => {
  const [isAscendent, setIsAscendent] = useState(defaultSortOrder?.order ? defaultSortOrder?.order === 'asc' : true);
  const [sortField, setSortField] = useState(defaultSortOrder?.sortBy ?? null);
  const [expandedRow, setExpandedRow] = useState(null);

  const sort = (sortBy) => {
    let nextIsAscendent;

    if (sortBy !== sortField) {
      // Use default if provided, else fallback to ascending
      nextIsAscendent = (defaultSortOrder[sortBy] ?? 'asc') === 'asc';
    } else {
      // Toggle for same field
      nextIsAscendent = !isAscendent;
    }

    setSortField(sortBy);
    setIsAscendent(nextIsAscendent);
    onSort({ field: sortBy, order: nextIsAscendent ? 'asc' : 'desc' });
  };

  const toggleRow = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const getTargetUrl = (data) => {
    // removed e.stopPropagation() to make the browser behave like normal
    if (typeOfData === 'other') {
      return null;
    }
    // navigate to the map view from routes table
    if (typeOfData === 'routes') {
      return `/${typeOfData}/${data.id}/edit?mode=map`;
    }
    if (typeOfData === 'contact history' || typeOfData === 'site history') {
      return null;
    }
    if (typeOfData === 'visits') {
      return `/sites/${data.site_id}/${typeOfData}/${data.id}`;
    }
    return `/${typeOfData}/${data.id}`;
  };

  const handleRowClick = (e, data) => {
    // pass the event to getTargetUrl to stop propagation
    const url = getTargetUrl(data);
    if (url) {
      window.location.href = url;
    }
  };

  const handleClick = (e, data) => {
    e.preventDefault();
    e.stopPropagation();

    if (typeOfData === 'seasons') {
      toggleRow(data.id); // Using toggleRow for expanding or collapsing a row in the table to show additional details
    } else if (typeOfData === 'notes') {
      onClick(data.id); // Using onClick to open a modal and sending the data id
    } else if (typeOfData === 'users') {
      onClick(data); // Using onClick to open a modal and sending the data
    } else if (typeOfData === 'items_for_distribution') { // Using onClick to redirect to sorted visit page
      onClick(data);
    } else {
      handleRowClick(e, data); // Using handleRowClick to open a specific link
    }
  };

  return (
    <table className={className}>
      <thead key="table-header">
        <tr
          className="w-full text-base font-semibold tracking-wide h-min-13 text-left text-vamos-black bg-vamos-v-light-gray"
          key="table-header-row"
        >
          {columns.map((column) => {
            return (
              <th
                className={`group px-4 py-3 ${column.className ? column.className : ''}`}
                key={column.title || column.className}
              >
                <div className={`${column.isSortable ? 'flex' : ''} ${column.headerClassName}`}>
                  <div className="w-fit">
                    {column.tooltip ? (
                      <div className="flex items-center gap-1">
                        {column.tooltip}
                        <span>{column.title.toUpperCase()}</span>
                      </div>
                    ) : (
                      column.title.toUpperCase()
                    )}
                  </div>
                  {/* this div helps to avoid 'jumping' when nte button appears */}
                  {column.isSortable && (
                    <div className="w-4 ml-1 opacity-0 group-hover:opacity-100">
                      <button
                        id={`sort-by-${column.dataIndex}`}
                        className="focus:outline-none"
                        type="button"
                        onClick={() => sort(column.dataIndex)}
                        aria-label={`sort by ${column.dataIndex}`}
                        data-testid={`sort-by-${column.dataIndex}`}
                      >
                        <MdOutlineSwapVert
                          size={16}
                          className="text-vamos-green1"
                        />
                      </button>
                    </div>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody key="table-body">
        {dataSource.map((data) => {
          const rowClickable = isRowClickable(data);

          return (
            <React.Fragment key={data.id}>
              <tr
                className={`text-vamos-black ${(!data.isEditing || !isHistory) && 'hover:bg-vamos-green3'} ${rowClickable ? 'cursor-pointer' : 'cursor-default'} min-h-13 even:bg-vamos-v-light-gray ${expandedRow === data.id && DetailComponent ? 'bg-vamos-green3 even:bg-vamos-green3 border-vamos-blue-gray border-t border-r border-l' : ''}`}
                key={data.id}
                onClick={(e) => {
                  if (!rowClickable) return;
                  handleClick(e, data);
                }}
                data-testid={`${typeOfData}-${data.id}-row`}
              >
                {columns.map((column) => {
                  let renderData = column.render ? column.render(data[column.dataIndex], data) : data[column.dataIndex];
                  if (column.dataIndex === 'description' && (typeof renderData === 'string' || typeof renderData === 'number')) {
                    renderData = convertUrlsToLinks(renderData);
                  }
                  return (
                    <td
                      className={`${column.className ? column.className : ''}`}
                    // long key to avoid warning when dnd applied in tabs
                      key={`${column.dataIndex}-${data.id}`}
                      data-testid={`${typeOfData}-${column.title.toLowerCase()}-cell`}
                    >
                      {isUsingLinkWrapper ? (column.dataIndex === 'action' || data.isEditing ? <div key="actions" onClick={(e) => { e.stopPropagation(); }} role="presentation">{renderData}</div> : (
                        <TableLink
                          href={getTargetUrl(data)}
                          className={`block w-full h-full px-4 py-3 ${rowClickable ? 'cursor-pointer' : 'cursor-default'} ${columnTextColor ? columnTextColor({ column, data }) : 'text-vamos-black'} hover:no-underline hover:text-vamos-black leading-normal whitespace-nowrap ${expandedRow === data.id && DetailComponent ? 'font-bold' : ''} ${column.className?.includes('truncate') ? 'truncate' : ''}`}
                          onClick={(e) => {
                            if (!rowClickable) return;
                            handleClick(e, data);
                          }}
                          renderData={renderData}
                        />
                      )
                      ) : (
                        <div className="px-4 py-3">
                          {renderData}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
              {DetailComponent && expandedRow === data.id && (
              <tr key={`detail-${data.id}`} className="border-vamos-blue-gray border-b border-r border-l">
                <td colSpan={columns.length}>
                  <DetailComponent data={data} />
                </td>
              </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

// PropTypes for component props validation
Table.propTypes = {
  dataSource: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataIndex: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      className: PropTypes.string,
      isSortable: PropTypes.bool,
      render: PropTypes.func,
      tooltip: PropTypes.node,
    }),
  ),
  onSort: PropTypes.func,
  typeOfData: PropTypes.oneOf(TABLE_DATA_TYPES),
  isHistory: PropTypes.bool,
  DetailComponent: PropTypes.elementType,
  isUsingLinkWrapper: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  columnTextColor: PropTypes.func,
  defaultSortOrder: PropTypes.object,
  isRowClickable: PropTypes.func,
};

export default Table;
